import React, { useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
	Field,
	FormSection,
	getFormValues,
	change,
	focus,
	getFormMeta
} from "redux-form";
import { get } from "lodash";

import { ReduxControls } from "rhombus-react";

import { noop } from "utils/common";

import {
	ASSET_TYPES,
	SOCIAL_AD_TYPES,
	DYNAMIC_SOCIAL_AD_TYPES,
	SOCIAL_FORMATS,
	CREATIVE_ASSET_TYPES
} from "utils/constants";

import { SOCIAL_WIZARD } from "widgets/forms/list";

import { getSharedCTAs, mapStringToOption, normalizeOptionToString } from "social/utils";
import { ALLOWED_CARD_TYPES_BY_ADTYPE } from "social/constants";
import { isDynamic } from "social/DynamicCreativeToggle";
import { assetType } from "social/BuildMethodRadio";

import InfoBanner from "widgets/InfoBanner";
import AssetSelect from "social/AssetSelect";
import { getDynamicCTAs } from "selectors/Social2";

const SHARED_FIELDS = {
	isDynamic,
	assetType,
	image: {
		name: "image",
		label: "Image",

		component: AssetSelect,
		assetType: ASSET_TYPES.IMAGE,
		accept: "image/*",
		tip: {
			description: "",
		},
	},
	video: {
		name: "video",
		label: "Video",

		component: AssetSelect,
		assetType: ASSET_TYPES.VIDEO,
		accept: ".mp4",
		tip: {
			description: "",
		},
	},
	title: {
		name: "title",
		label: "Headline / Title",
		placeholder: "Enter a short headline",
		className: "full-width",
		component: ReduxControls.Input,
	},
	primaryText: {
		name: "primaryText",
		label: "Primary Text",
		placeholder: "Tell people what your creative is about",
		className: "full-width",
		component: ReduxControls.Input,
	},
	description: {
		name: "description",
		label: "Description",
		placeholder: "Include additional details",
		className: "full-width",
		component: ReduxControls.Input,
	},
	landingPage: {
		name: "landingPage",
		label: "Landing Page",
		placeholder: "https://www.example.com",
		className: "full-width",
		component: ReduxControls.Input,
	},
	displayLink: {
		name: "displayLink",
		label: "Display Link",
		placeholder: "Enter link text to be displayed",
		className: "half-width",
		component: ReduxControls.Input,
	},
	cta: {
		name: "cta",
		label: "CTA",
		placeholder: "Enter Call to Action Text",
		className: "half-width",
		component: ReduxControls.Select,
		menuPlacement: "top",
		onMenuScrollToBottom: noop,
	},
};

const SHARED_FIELD_LIST = Object.values(SHARED_FIELDS); // Used to keep the fields ordered

/*
	This maps an adType's fields to the Shared Content field that it gets its value populated from.

	This is used both to determine what set of Shared Content fields to show, and to propagate values
	from the Shared Content form to the adType-specific forms.
*/
const SHARED_FIELDS_MAP = {
	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE]: {
		image: SHARED_FIELDS.image,
		headline: SHARED_FIELDS.title,
		description: SHARED_FIELDS.description,
		primaryText: SHARED_FIELDS.primaryText,
		landingPage: SHARED_FIELDS.landingPage,
		displayLink: SHARED_FIELDS.displayLink,
		cta: SHARED_FIELDS.cta,
	},
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO]: {
		video: SHARED_FIELDS.video,
		headline: SHARED_FIELDS.title,
		description: SHARED_FIELDS.description,
		primaryText: SHARED_FIELDS.primaryText,
		landingPage: SHARED_FIELDS.landingPage,
		displayLink: SHARED_FIELDS.displayLink,
		cta: SHARED_FIELDS.cta,
	},
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL]: {
		primaryText: SHARED_FIELDS.primaryText,
		cta: SHARED_FIELDS.cta,

		"cards[0].image": SHARED_FIELDS.image,
		"cards[0].video": SHARED_FIELDS.video,
		"cards[0].headline": SHARED_FIELDS.title,
		"cards[0].description": SHARED_FIELDS.description,
		"cards[0].landingPage": SHARED_FIELDS.landingPage,
	},

	[SOCIAL_AD_TYPES.FACEBOOK_IMAGE_MO]: {
		isDynamic: SHARED_FIELDS.isDynamic,
		assetType: SHARED_FIELDS.assetType,
		image: SHARED_FIELDS.image,
		headline: SHARED_FIELDS.title,
		description: SHARED_FIELDS.description,
		primaryText: SHARED_FIELDS.primaryText,
		landingPage: SHARED_FIELDS.landingPage,
		displayLink: SHARED_FIELDS.displayLink,
		cta: SHARED_FIELDS.cta,
	},
	[SOCIAL_AD_TYPES.FACEBOOK_VIDEO_MO]: {
		isDynamic: SHARED_FIELDS.isDynamic,
		assetType: SHARED_FIELDS.assetType,
		video: SHARED_FIELDS.video,
		thumbnail: SHARED_FIELDS.image,
		headline: SHARED_FIELDS.title,
		description: SHARED_FIELDS.description,
		primaryText: SHARED_FIELDS.primaryText,
		landingPage: SHARED_FIELDS.landingPage,
		displayLink: SHARED_FIELDS.displayLink,
		cta: SHARED_FIELDS.cta,
	},
	[SOCIAL_AD_TYPES.FACEBOOK_CAROUSEL_MO]: {
		isDynamic: SHARED_FIELDS.isDynamic,
		assetType: SHARED_FIELDS.assetType,
		primaryText: SHARED_FIELDS.primaryText,
		cta: SHARED_FIELDS.cta,

		"cards[0].image": SHARED_FIELDS.image,
		"cards[0].video": SHARED_FIELDS.video,
		"cards[0].headline": SHARED_FIELDS.title,
		"cards[0].description": SHARED_FIELDS.description,
		"cards[0].landingPage": SHARED_FIELDS.landingPage,
	},

	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_IMAGE]: {
		"images[0]": SHARED_FIELDS.image,
		"headlines[0]": SHARED_FIELDS.title,
		"descriptions[0]": SHARED_FIELDS.description,
		landingPage: SHARED_FIELDS.landingPage,
		cta: SHARED_FIELDS.cta,
	},
	[SOCIAL_AD_TYPES.GOOGLE_DEMAND_GEN_CAROUSEL]: {
		"cards[0].images.square": SHARED_FIELDS.image,
		headline: SHARED_FIELDS.title,
		"cards[0].headline": SHARED_FIELDS.title,
		description: SHARED_FIELDS.description,
		landingPage: SHARED_FIELDS.landingPage,
		"cards[0].landingPage": SHARED_FIELDS.landingPage,
		"cards[0].cta": SHARED_FIELDS.cta,
	},

	[SOCIAL_AD_TYPES.LINKEDIN_IMAGE]: {
		isDynamic: SHARED_FIELDS.isDynamic,
		assetType: SHARED_FIELDS.assetType,
		image: SHARED_FIELDS.image,
		headline: SHARED_FIELDS.title,
		intro: SHARED_FIELDS.primaryText,
		landingPage: SHARED_FIELDS.landingPage,
	},
	[SOCIAL_AD_TYPES.LINKEDIN_VIDEO]: {
		isDynamic: SHARED_FIELDS.isDynamic,
		assetType: SHARED_FIELDS.assetType,
		video: SHARED_FIELDS.video,
		thumbnail: SHARED_FIELDS.image,
		headline: SHARED_FIELDS.title,
		intro: SHARED_FIELDS.primaryText,
		landingPage: SHARED_FIELDS.landingPage,
		cta: SHARED_FIELDS.cta,
	},
	[SOCIAL_AD_TYPES.LINKEDIN_CAROUSEL]: {
		isDynamic: SHARED_FIELDS.isDynamic,
		assetType: SHARED_FIELDS.assetType,
		landingPage: SHARED_FIELDS.landingPage,
		introDescription: SHARED_FIELDS.primaryText,

		"cards[0].image": SHARED_FIELDS.image,
		"cards[0].headline": SHARED_FIELDS.title,
		"cards[0].landingPage": SHARED_FIELDS.landingPage,
	},

	[SOCIAL_AD_TYPES.PINTEREST_IMAGE]: {
		isDynamic: SHARED_FIELDS.isDynamic,
		assetType: SHARED_FIELDS.assetType,
		image: SHARED_FIELDS.image,
		title: SHARED_FIELDS.title,
		description: SHARED_FIELDS.description,
		landingPage: SHARED_FIELDS.landingPage,
	},
	[SOCIAL_AD_TYPES.PINTEREST_VIDEO]: {
		isDynamic: SHARED_FIELDS.isDynamic,
		assetType: SHARED_FIELDS.assetType,
		video: SHARED_FIELDS.video,
		thumbnail: SHARED_FIELDS.image,
		title: SHARED_FIELDS.title,
		description: SHARED_FIELDS.description,
		landingPage: SHARED_FIELDS.landingPage,
	},
	[SOCIAL_AD_TYPES.PINTEREST_MAX_WIDTH_VIDEO]: {
		isDynamic: SHARED_FIELDS.isDynamic,
		assetType: SHARED_FIELDS.assetType,
		video: SHARED_FIELDS.video,
		thumbnail: SHARED_FIELDS.image,
		title: SHARED_FIELDS.title,
		description: SHARED_FIELDS.description,
		landingPage: SHARED_FIELDS.landingPage,
	},
	[SOCIAL_AD_TYPES.PINTEREST_CAROUSEL]: {
		isDynamic: SHARED_FIELDS.isDynamic,
		assetType: SHARED_FIELDS.assetType,
		"cards[0].image": SHARED_FIELDS.image,
		"cards[0].title": SHARED_FIELDS.title,
		"cards[0].description": SHARED_FIELDS.description,
		"cards[0].landingPage": SHARED_FIELDS.landingPage,
	},

	[SOCIAL_AD_TYPES.SNAPCHAT_IMAGE]: {
		isDynamic: SHARED_FIELDS.isDynamic,
		assetType: SHARED_FIELDS.assetType,
		image: SHARED_FIELDS.image,
		headline: SHARED_FIELDS.title,
		landingPage: SHARED_FIELDS.landingPage,
		cta: SHARED_FIELDS.cta,
	},
	[SOCIAL_AD_TYPES.SNAPCHAT_VIDEO]: {
		isDynamic: SHARED_FIELDS.isDynamic,
		assetType: SHARED_FIELDS.assetType,
		video: SHARED_FIELDS.video,
		headline: SHARED_FIELDS.title,
		landingPage: SHARED_FIELDS.landingPage,
		cta: SHARED_FIELDS.cta,
	},

	[SOCIAL_AD_TYPES.TWITTER_IMAGE]: {
		image: SHARED_FIELDS.image,
		headline: SHARED_FIELDS.title,
		tweet: SHARED_FIELDS.primaryText,
		landingPage: SHARED_FIELDS.landingPage,
	},
	[SOCIAL_AD_TYPES.TWITTER_VIDEO]: {
		video: SHARED_FIELDS.video,
		headline: SHARED_FIELDS.title,
		tweet: SHARED_FIELDS.primaryText,
		landingPage: SHARED_FIELDS.landingPage,
	},
	[SOCIAL_AD_TYPES.YOUTUBE_INSTREAM]: {
		video: SHARED_FIELDS.video,
		thumbnail: SHARED_FIELDS.image,
	},

	[SOCIAL_AD_TYPES.TIKTOK_VIDEO]: {
		isDynamic: SHARED_FIELDS.isDynamic,
		assetType: SHARED_FIELDS.assetType,
		video: SHARED_FIELDS.video,
		thumbnail: SHARED_FIELDS.image,
		primaryText: SHARED_FIELDS.primaryText,
		landingPage: SHARED_FIELDS.landingPage,
		cta: SHARED_FIELDS.cta,
	},
};

function getSharedFields(adTypes){
	const sharedFieldSet = new Set();

	for(let i=0; i<adTypes.length; i++){
		const adType = adTypes[i];
		const map = SHARED_FIELDS_MAP[adType];
		if(map){
			const fields = Object.values(map);
			fields.forEach(field => sharedFieldSet.add(field));

			if(sharedFieldSet.size === SHARED_FIELD_LIST.length){
				// If the Set already contains every possible shared field, we can stop iteration early
				break;
			}
		}
	}

	// Iterate over SHARED_FIELD_LIST to guarantee fields are in the correct order
	return SHARED_FIELD_LIST.filter(field => sharedFieldSet.has(field));
}

export default function SharedContent(props){
	const { navigationActions, libraryId } = props;
	const dispatch = useDispatch();

	const values = useSelector(getFormValues(SOCIAL_WIZARD));
	const meta = useSelector(getFormMeta(SOCIAL_WIZARD));
	const dynamicCTAs = useSelector(getDynamicCTAs);
	const adTypes = values?.adTypes || [];
	const sharedAssetType = values?.shared?.assetType;

	const ctaOptions = useMemo(() => {
		return getSharedCTAs(adTypes, dynamicCTAs);
	}, [adTypes]);

	const sharedFields = useMemo(() => {
		return getSharedFields(adTypes);
	}, [adTypes]);

	const creativeFieldsBySharedName = useMemo(() => {
		/*
			This just gives us a reversed SHARED_FIELDS_MAP, so we don't have to iterate as much to do updates.

			It's an object that looks like:
			{
				["shared.cta"]: [
					'facebookImage.cta',
					'facebookVideo.cta',
					'linkedInSponsoredVideo.cta',
					'snapWebViewImage.cta',
					'snapWebViewVideo.cta',
					'youTubeInStream.cta'
				],
				...
			}
			Each entry provides us a specific list of values to change, if necessary, after a Shared Content
			field is updated.
		*/
		return adTypes.reduce((prev, adType) => {
			const map = SHARED_FIELDS_MAP[adType];
			Object.entries(map).forEach(([creativeFieldName, sharedField]) => {
				const sharedFieldName = `shared.${sharedField.name}`;
				if(!prev[sharedFieldName]){
					prev[sharedFieldName] = [];
				}
				prev[sharedFieldName].push(`${adType}.${creativeFieldName}`);
			});
			return prev;
		}, {});
	}, [adTypes]);

	function initializeCardType(field, value){
		/*
			Carousel cards initialize as images, but if the user sets a `video` value in Shared Content,
			we have to set any linked cards to be videos.

			If the asset is an image, it's fine to leave the value, because cards default to images.
			If the asset is a video, set the card's type to video, if the platform's carousels allow it.
		*/
		const cardAssetMatches = /^(?<card>.+cards\[\d+\])\.video$/.exec(field); /*
			To verify we need to act, we have to check that the `field` looks like:
				"someCarouselType.cards[<digits>].video"

			And we capture everything before ".video" as a named `card` group,
			which allows us to inspect/change the card's `type` field at:
				"someCarouselType.cards[<digits>].type"
		*/
		if(!cardAssetMatches){ return; }

		const { card } = cardAssetMatches.groups;
		const [adType] = card.split(".");

		const typePath = `${card}.type`;
		const cardType = get(values, typePath);
		const visited = get(meta, typePath)?.visited;

		const isAllowed = ALLOWED_CARD_TYPES_BY_ADTYPE[adType].includes(SOCIAL_FORMATS.VIDEO);

		if((cardType === SOCIAL_FORMATS.IMAGE) && !visited && isAllowed){
			// We only want to change the card's type once, so we also `focus` to mark it as visited.
			dispatch(change(SOCIAL_WIZARD, typePath, SOCIAL_FORMATS.VIDEO, true));
			dispatch(focus(SOCIAL_WIZARD, typePath));
		}
	}

	function updateCreatives(alsoValue, value, previousValue, name){ /*
			The first argument to a redux-form Field onChange handler is supposed to be the change event,
			but this appears to get overwritten by the value in Rhombus.
		*/
		const creativeFields = creativeFieldsBySharedName[name];

		creativeFields.forEach((field) => {
			const { visited } = get(meta, field) || {}; /*
				We use visited as our check instead of touched, since we will set the field as touched
				in our change dispatch.
			*/

			if(!visited){
				const [adType] = field.split(".");
				const isDynamicType = DYNAMIC_SOCIAL_AD_TYPES.includes(adType);
				let valueForType = value;
				if(isDynamicType && name !== "shared.assetType" && name !== "shared.isDynamic"){
					if(/image|video/i.test(name)){
						valueForType = {
							...value,
							isDynamic: false,
						};
					} else {
						valueForType = {
							isDynamic: false,
							value,
						};
					}
				}
				initializeCardType(field, valueForType);
				dispatch(change(SOCIAL_WIZARD, field, valueForType, true)); /*
					We pass the fourth argument as `true` to mark the field as touched, so it shows warnings right away.
				*/
			}
		});
	}

	return (
		<div className="form-container">
			<InfoBanner>
				Assets added to the shared content tab will auto-populate the corresponding fields for each of the selected platform formats.
			</InfoBanner>
			<FormSection name="shared">
				<div className="fields">
					{sharedFields.map((field) => {
						const assetSelectProps = (field.component === AssetSelect) ? {
							libraryId,
							navigationActions,
							isAdStudio: sharedAssetType === CREATIVE_ASSET_TYPES.ADSTUDIO,
						} : {};
						const ctaProps = (field.name === "cta") ? {
							options: ctaOptions,
							normalize: normalizeOptionToString,
							format: mapStringToOption,
						} : {};

						return (
							<Field
								key = {field.name}
								{...field}
								onChange = {updateCreatives}

								{...assetSelectProps}
								{...ctaProps}
							/>
						);
					})}
				</div>
			</FormSection>
		</div>
	);
}

SharedContent.displayName = "SharedContentForm";
